/**
 * /job-cost — construction-industry project cost reporting.
 *
 * Four tabs all backed by existing SQL views
 * (job_cost_summary_v / wip_v / committed_cost_v / project_profitability_v):
 *
 *   Summary         Phase × cost-type grid. Original + approved CO +
 *                   revised budget → committed → actual → cost-to-complete
 *                   → EAC → variance → %. The Mirage "job master" report.
 *   WIP             Project-level over/underbilling snapshot. Earned
 *                   revenue (%·complete × contract) vs billed-to-date →
 *                   positive = underbilled (good for cash); negative =
 *                   overbilled (liability on BS).
 *   Committed       Subcontract commitments + billed/paid/retention.
 *   Profitability   Per-project revised contract · revised budget ·
 *                   actual · EAC · estimated gross profit · GP%.
 *
 * Optional project filter at the top — default shows all active
 * projects across the current entity (the reports service scopes
 * via entity_context).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tab, Tabs } from "@mui/material";

import {
  committedCostQueryOptions,
  costCodeReportQueryOptions,
  jobCostSummaryQueryOptions,
  projectProfitabilityQueryOptions,
  wipQueryOptions,
} from "@/lib/queries";
import { ProjectIdSelect } from "@/components/ProjectIdSelect";
import type {
  CommittedCostRow,
  CostCodeReportRow,
  JobCostSummaryRow,
  ProjectProfitabilityRow,
  WipRow,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/job-cost")({
  component: JobCostPage,
});

type TabKey = "summary" | "wip" | "committed" | "profitability" | "by-code";


function JobCostPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const [tab, setTab] = useState<TabKey>("summary");
  const [projectFilter, setProjectFilter] = useState("");
  const projectId = projectFilter || undefined;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Job Cost</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Budget · Committed · Actual · EAC · %Complete by phase
          </span>
          <div className="flex-1" />
          <div className="w-[320px]">
          <ProjectIdSelect
            value={projectFilter}
            onChange={setProjectFilter}
            statusFilter={["active"]}
            label="Project"
            placeholder="All active projects…"
          />
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs value={tab} onChange={(_e, v) => setTab(v as TabKey)} sx={{ minHeight: 40 }}>
          <Tab value="summary" label="Cost Summary" sx={{ minHeight: 40 }} />
          <Tab value="by-code" label="By Cost Code" sx={{ minHeight: 40 }} />
          <Tab value="wip" label="WIP Schedule" sx={{ minHeight: 40 }} />
          <Tab value="committed" label="Committed Cost" sx={{ minHeight: 40 }} />
          <Tab value="profitability" label="Profitability" sx={{ minHeight: 40 }} />
        </Tabs>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {tab === "summary" && <SummaryTab projectId={projectId} />}
        {tab === "by-code" && <ByCodeTab projectId={projectId} />}
        {tab === "wip" && <WipTab projectId={projectId} />}
        {tab === "committed" && <CommittedTab projectId={projectId} />}
        {tab === "profitability" && <ProfitabilityTab projectId={projectId} />}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Shared formatters
// ──────────────────────────────────────────────────────────────
function money(v: string | number): string {
  const n = typeof v === "string" ? Number(v) : v;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
function moneySigned(v: string | number): string {
  const n = typeof v === "string" ? Number(v) : v;
  return (n >= 0 ? "" : "−") + Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
function pct(v: string | number): string {
  const n = typeof v === "string" ? Number(v) : v;
  return `${n.toFixed(1)}%`;
}


// ──────────────────────────────────────────────────────────────
// Cost Summary — phase × cost_type grid, grouped by project
// ──────────────────────────────────────────────────────────────
function SummaryTab({ projectId }: { projectId?: string }) {
  const q = useQuery(jobCostSummaryQueryOptions(projectId));
  const rows = q.data ?? [];

  // Group by project → phase
  const grouped = useMemo(() => {
    const byProject: Record<string, {
      code: string; name: string;
      phases: Record<string, {
        code: string; name: string;
        rows: JobCostSummaryRow[];
      }>;
    }> = {};
    for (const r of rows) {
      const p = byProject[r.project_id] ??= {
        code: r.project_code, name: r.project_name, phases: {},
      };
      const ph = p.phases[r.phase_id] ??= {
        code: r.phase_code, name: r.phase_name, rows: [],
      };
      ph.rows.push(r);
    }
    return byProject;
  }, [rows]);

  if (q.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
        No job cost data yet.
        <br/>
        <span className="text-xs">Job costs populate as AP invoices, subcontract billings, and labor accruals post against projects.</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.values(grouped).map((proj) => {
        // Roll up project totals
        const pTotal = Object.values(proj.phases).flatMap((ph) => ph.rows).reduce(
          (acc, r) => ({
            revised: acc.revised + Number(r.revised_budget),
            committed: acc.committed + Number(r.committed),
            actual: acc.actual + Number(r.actual_cost),
            eac: acc.eac + Number(r.eac),
            variance: acc.variance + Number(r.variance),
          }),
          { revised: 0, committed: 0, actual: 0, eac: 0, variance: 0 },
        );
        return (
          <div key={proj.code} className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-baseline gap-3 dark:bg-gray-950 dark:border-gray-800">
              <span className="font-mono text-sm font-semibold">{proj.code}</span>
              <span className="text-sm text-slate-800 dark:text-slate-200">{proj.name}</span>
              <div className="flex-1" />
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Revised <span className="font-mono">${money(pTotal.revised)}</span>
                &nbsp;·&nbsp; Actual <span className="font-mono">${money(pTotal.actual)}</span>
                &nbsp;·&nbsp; EAC <span className="font-mono">${money(pTotal.eac)}</span>
                &nbsp;·&nbsp; Variance{" "}
                <span className={
                  "font-mono " + (pTotal.variance < 0 ? "text-red-700" : "text-emerald-700")
                }>
                  ${moneySigned(pTotal.variance)}
                </span>
              </span>
            </div>

            <table className="w-full text-xs">
              <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                <tr>
                  <th className="text-left px-4 py-2">Phase / Cost Type</th>
                  <th className="text-right px-3 py-2">Original</th>
                  <th className="text-right px-3 py-2">Apprvd CO</th>
                  <th className="text-right px-3 py-2">Revised</th>
                  <th className="text-right px-3 py-2">Committed</th>
                  <th className="text-right px-3 py-2">Actual</th>
                  <th className="text-right px-3 py-2">Cost to Complete</th>
                  <th className="text-right px-3 py-2">EAC</th>
                  <th className="text-right px-3 py-2">Variance</th>
                  <th className="text-right px-3 py-2">%C</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(proj.phases).map((ph) => (
                  <Fragment key={`ph-${proj.code}-${ph.code}`}>
                    <tr className="bg-slate-50 dark:bg-gray-950">
                      <td className="px-4 py-1.5 font-semibold text-slate-700 dark:text-slate-300">
                        {ph.code} — {ph.name}
                      </td>
                      <td colSpan={9}></td>
                    </tr>
                    {ph.rows.map((r) => {
                      const varNeg = Number(r.variance) < 0;
                      return (
                        <tr
                          key={`${r.phase_id}-${r.cost_type}`}
                          className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800"
                        >
                          <td className="pl-8 pr-4 py-1 text-slate-600 capitalize dark:text-slate-300">
                            {r.cost_type}
                          </td>
                          <td className="px-3 py-1 text-right font-mono">{money(r.original_budget)}</td>
                          <td className="px-3 py-1 text-right font-mono">{money(r.approved_co_budget)}</td>
                          <td className="px-3 py-1 text-right font-mono font-semibold">{money(r.revised_budget)}</td>
                          <td className="px-3 py-1 text-right font-mono">{money(r.committed)}</td>
                          <td className="px-3 py-1 text-right font-mono">{money(r.actual_cost)}</td>
                          <td className="px-3 py-1 text-right font-mono text-slate-500 dark:text-slate-400">{money(r.cost_to_complete)}</td>
                          <td className="px-3 py-1 text-right font-mono">{money(r.eac)}</td>
                          <td className={"px-3 py-1 text-right font-mono " + (varNeg ? "text-red-700" : "text-emerald-700")}>
                            {moneySigned(r.variance)}
                          </td>
                          <td className="px-3 py-1 text-right font-mono text-slate-500 dark:text-slate-400">{pct(r.pct_complete)}</td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// By Cost Code — Mirage-style hours + $ rollup per code × cost type
// ──────────────────────────────────────────────────────────────
function ByCodeTab({ projectId }: { projectId?: string }) {
  const q = useQuery(costCodeReportQueryOptions(projectId));
  const rows: CostCodeReportRow[] = q.data ?? [];

  const grouped = useMemo(() => {
    // Group by project, then sort within each project by cost_code
    const byProject: Record<string, {
      code: string; name: string;
      rows: CostCodeReportRow[];
    }> = {};
    for (const r of rows) {
      const p = byProject[r.project_id] ??= {
        code: r.project_code, name: r.project_name, rows: [],
      };
      p.rows.push(r);
    }
    return byProject;
  }, [rows]);

  if (q.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
        No cost-code data yet.
        <br />
        <span className="text-xs">
          Tag job budgets, time entries, and AP lines with a cost code to populate this view.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.values(grouped).map((proj) => {
        const totals = proj.rows.reduce(
          (acc, r) => ({
            bh: acc.bh + Number(r.budgeted_hours),
            ah: acc.ah + Number(r.actual_hours),
            bc: acc.bc + Number(r.budgeted_cost),
            ac: acc.ac + Number(r.actual_cost),
          }),
          { bh: 0, ah: 0, bc: 0, ac: 0 },
        );
        const hoursVar = totals.bh - totals.ah;
        const costVar = totals.bc - totals.ac;

        return (
          <div key={proj.code} className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-baseline gap-3 dark:bg-gray-950 dark:border-gray-800">
              <span className="font-mono text-sm font-semibold">{proj.code}</span>
              <span className="text-sm text-slate-800 dark:text-slate-200">{proj.name}</span>
              <div className="flex-1" />
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Hrs: <span className="font-mono">{totals.ah.toFixed(1)}</span>
                /<span className="font-mono">{totals.bh.toFixed(1)}</span>
                {" "}(<span className={hoursVar < 0 ? "text-red-700" : "text-emerald-700"}>
                  {hoursVar >= 0 ? "+" : "−"}{Math.abs(hoursVar).toFixed(1)}
                </span>)
                &nbsp;·&nbsp; $: <span className="font-mono">{money(totals.ac)}</span>
                /<span className="font-mono">{money(totals.bc)}</span>
                {" "}(<span className={costVar < 0 ? "text-red-700" : "text-emerald-700"}>
                  ${moneySigned(costVar)}
                </span>)
              </span>
            </div>

            <table className="w-full text-xs">
              <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
                <tr>
                  <th className="text-left px-4 py-2">Cost Code</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Bdgt Hrs</th>
                  <th className="text-right px-3 py-2">Act Hrs</th>
                  <th className="text-right px-3 py-2">Hrs Var</th>
                  <th className="text-right px-3 py-2">Bdgt $</th>
                  <th className="text-right px-3 py-2">Act $</th>
                  <th className="text-right px-3 py-2">$ Var</th>
                  <th className="text-right px-3 py-2">$/Hr (act)</th>
                </tr>
              </thead>
              <tbody>
                {proj.rows.map((r, i) => {
                  const ah = Number(r.actual_hours);
                  const ac = Number(r.actual_cost);
                  const hVar = Number(r.hours_variance);
                  const cVar = Number(r.cost_variance);
                  const rate = ah > 0 ? ac / ah : 0;
                  return (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800">
                      <td className="px-4 py-1">
                        {r.cost_code ? (
                          <>
                            <span className="font-mono text-xs font-semibold">{r.cost_code}</span>
                            <span className="text-slate-500 ml-2 dark:text-slate-400">{r.cost_code_name}</span>
                          </>
                        ) : (
                          <span className="text-amber-700 italic">— uncoded —</span>
                        )}
                      </td>
                      <td className="px-3 py-1 text-xs capitalize text-slate-600 dark:text-slate-300">
                        {r.cost_type ?? "—"}
                      </td>
                      <td className="px-3 py-1 text-right font-mono">{Number(r.budgeted_hours).toFixed(1)}</td>
                      <td className="px-3 py-1 text-right font-mono font-semibold">{ah.toFixed(1)}</td>
                      <td className={"px-3 py-1 text-right font-mono " + (hVar < 0 ? "text-red-700" : "text-emerald-700")}>
                        {hVar >= 0 ? "+" : "−"}{Math.abs(hVar).toFixed(1)}
                      </td>
                      <td className="px-3 py-1 text-right font-mono">{money(r.budgeted_cost)}</td>
                      <td className="px-3 py-1 text-right font-mono font-semibold">{money(r.actual_cost)}</td>
                      <td className={"px-3 py-1 text-right font-mono " + (cVar < 0 ? "text-red-700" : "text-emerald-700")}>
                        {moneySigned(r.cost_variance)}
                      </td>
                      <td className="px-3 py-1 text-right font-mono text-slate-500 dark:text-slate-400">
                        {ah > 0 ? `$${rate.toFixed(2)}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
      <div className="text-[11px] text-slate-500 px-1 dark:text-slate-400">
        Hours come from posted + unposted time entries (so weekly variance shows immediately).
        Dollars come from posted journal lines only. "$/Hr (act)" is loaded labor cost rate when
        actuals exist.
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// WIP Schedule — the accountant / surety view
// ──────────────────────────────────────────────────────────────
function WipTab({ projectId }: { projectId?: string }) {
  const q = useQuery(wipQueryOptions(projectId));
  const rows: WipRow[] = q.data ?? [];

  if (q.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;

  const totals = rows.reduce(
    (acc, r) => ({
      contract: acc.contract + Number(r.revised_contract),
      cost: acc.cost + Number(r.cost_incurred),
      earned: acc.earned + Number(r.earned_revenue),
      billed: acc.billed + Number(r.billed_to_date),
      overUnder: acc.overUnder + Number(r.over_under_billing),
    }),
    { contract: 0, cost: 0, earned: 0, billed: 0, overUnder: 0 },
  );

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden dark:bg-gray-900 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-2">Project</th>
            <th className="text-right px-3 py-2">Orig Contract</th>
            <th className="text-right px-3 py-2">Apprvd CO</th>
            <th className="text-right px-3 py-2">Revised Contract</th>
            <th className="text-right px-3 py-2">Cost Incurred</th>
            <th className="text-right px-3 py-2">EAC</th>
            <th className="text-right px-3 py-2">%C</th>
            <th className="text-right px-3 py-2">Earned Revenue</th>
            <th className="text-right px-3 py-2">Billed to Date</th>
            <th className="text-right px-3 py-2">Over/(Under) Billing</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-12 text-center text-slate-500 dark:text-slate-400">
                No WIP data. Projects populate here once they have budgets + actuals.
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const overUnder = Number(r.over_under_billing);
            return (
              <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800">
                <td className="px-4 py-1.5">
                  <div className="font-mono text-xs font-semibold">{r.project_code}</div>
                  <div className="text-xs text-slate-600 truncate max-w-[200px] dark:text-slate-300">{r.project_name}</div>
                </td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.original_contract_amount)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.approved_change_orders)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs font-semibold">{money(r.revised_contract)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.cost_incurred)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.eac)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs text-slate-500 dark:text-slate-400">{pct(r.pct_complete)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.earned_revenue)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.billed_to_date)}</td>
                <td className={
                  "px-3 py-1 text-right font-mono text-xs " + (
                    overUnder > 0 ? "text-amber-700" :      // overbilled — liability
                      overUnder < 0 ? "text-emerald-700" :  // underbilled — asset (good for cash)
                        "text-slate-500"
                  )
                }>
                  {moneySigned(r.over_under_billing)}
                </td>
              </tr>
            );
          })}
          {rows.length > 0 && (
            <tr className="border-t-2 border-slate-400 bg-slate-100 font-semibold dark:bg-gray-800">
              <td className="px-4 py-2">TOTALS</td>
              <td></td>
              <td></td>
              <td className="px-3 py-2 text-right font-mono">{money(totals.contract)}</td>
              <td className="px-3 py-2 text-right font-mono">{money(totals.cost)}</td>
              <td></td>
              <td></td>
              <td className="px-3 py-2 text-right font-mono">{money(totals.earned)}</td>
              <td className="px-3 py-2 text-right font-mono">{money(totals.billed)}</td>
              <td className={
                "px-3 py-2 text-right font-mono " + (
                  totals.overUnder > 0 ? "text-amber-700" :
                    totals.overUnder < 0 ? "text-emerald-700" : "text-slate-500"
                )
              }>
                {moneySigned(totals.overUnder)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rows.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-500 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-400">
          Over/(under) billing = billed-to-date − earned revenue. Positive (amber) = overbilled (liability: billings in excess of costs).
          Negative (emerald) = underbilled (asset: costs + earnings in excess of billings).
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Committed Cost — per subcontract
// ──────────────────────────────────────────────────────────────
function CommittedTab({ projectId }: { projectId?: string }) {
  const q = useQuery(committedCostQueryOptions(projectId));
  const rows: CommittedCostRow[] = q.data ?? [];
  if (q.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden dark:bg-gray-900 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-2">Subcontract</th>
            <th className="text-left px-3 py-2">Project</th>
            <th className="text-left px-3 py-2">Vendor</th>
            <th className="text-right px-3 py-2">Original</th>
            <th className="text-right px-3 py-2">Apprvd CO</th>
            <th className="text-right px-3 py-2">Revised</th>
            <th className="text-right px-3 py-2">Billed</th>
            <th className="text-right px-3 py-2">Paid</th>
            <th className="text-right px-3 py-2">Retention</th>
            <th className="text-right px-3 py-2">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-12 text-center text-slate-500 dark:text-slate-400">
                No subcontracts. Create one under a project to commit costs.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.subcontract_id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800">
              <td className="px-4 py-1.5 font-mono text-xs font-semibold">{r.subcontract_no}</td>
              <td className="px-3 py-1 text-xs">
                <span className="font-mono">{r.project_code}</span>
                <span className="text-slate-500 ml-1 truncate dark:text-slate-400">{r.project_name}</span>
              </td>
              <td className="px-3 py-1 text-xs">
                <span className="font-mono">{r.vendor_code}</span>
                <span className="text-slate-500 ml-1 truncate dark:text-slate-400">{r.vendor_name}</span>
              </td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(r.original_amount)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(r.approved_co_amount)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs font-semibold">{money(r.revised_amount)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(r.billed_to_date)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(r.paid_to_date)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs text-amber-700">{money(r.retention_held)}</td>
              <td className="px-3 py-1 text-right font-mono text-xs">{money(r.remaining_to_bill)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Profitability — per project
// ──────────────────────────────────────────────────────────────
function ProfitabilityTab({ projectId }: { projectId?: string }) {
  const q = useQuery(projectProfitabilityQueryOptions(projectId));
  const rows: ProjectProfitabilityRow[] = q.data ?? [];
  if (q.isLoading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden dark:bg-gray-900 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-2">Project</th>
            <th className="text-right px-3 py-2">Revised Contract</th>
            <th className="text-right px-3 py-2">Revised Budget</th>
            <th className="text-right px-3 py-2">Actual Cost</th>
            <th className="text-right px-3 py-2">EAC</th>
            <th className="text-right px-3 py-2">Est. GP</th>
            <th className="text-right px-3 py-2">GP %</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-12 text-center text-slate-500 dark:text-slate-400">
                No projects to report on.
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const gp = Number(r.est_gross_profit);
            const gpPct = Number(r.gp_pct);
            return (
              <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800">
                <td className="px-4 py-1.5">
                  <div className="font-mono text-xs font-semibold">{r.project_code}</div>
                  <div className="text-xs text-slate-600 truncate max-w-[240px] dark:text-slate-300">{r.project_name}</div>
                </td>
                <td className="px-3 py-1 text-right font-mono text-xs font-semibold">{money(r.revised_contract)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.revised_budget)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.actual_cost)}</td>
                <td className="px-3 py-1 text-right font-mono text-xs">{money(r.eac)}</td>
                <td className={
                  "px-3 py-1 text-right font-mono text-xs font-semibold " + (
                    gp < 0 ? "text-red-700" : "text-emerald-700"
                  )
                }>
                  {moneySigned(r.est_gross_profit)}
                </td>
                <td className={
                  "px-3 py-1 text-right font-mono text-xs " + (
                    gpPct < 0 ? "text-red-700"
                      : gpPct < 5 ? "text-amber-700"
                      : "text-emerald-700"
                  )
                }>
                  {pct(r.gp_pct)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
